/**
 * Custom SVG illustrations for empty states.
 * Uses Field Olive palette (primary/accent CSS variables) with subtle motion.
 *
 * Each illustration accepts:
 *   - className: Tailwind classes for sizing
 *   - animate: boolean (default true) — enables Framer Motion entry animations
 */
import { motion } from 'framer-motion';

const float = {
  initial: { y: 4 },
  animate: { y: -4 },
  transition: { duration: 2.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
};

const pop = {
  initial: { scale: 0.6, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { type: 'spring', stiffness: 220, damping: 18 },
};

const Wrap = ({ children, className = 'w-40 h-40' }) => (
  <motion.div className={className} {...pop} data-testid="empty-illustration">
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* No expenses — floating receipt with money                          */
/* ------------------------------------------------------------------ */
export const EmptyExpenses = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Soft halo */}
      <ellipse cx="100" cy="170" rx="62" ry="8" fill="hsl(var(--primary) / 0.08)" />

      {/* Receipt body */}
      <motion.g {...float}>
        <path
          d="M55 40 L145 40 L145 158 L132 150 L120 158 L108 150 L96 158 L84 150 L72 158 L60 150 L55 158 Z"
          fill="hsl(var(--card))"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* Lines on receipt */}
        <rect x="68" y="58" width="64" height="6" rx="3" fill="hsl(var(--primary) / 0.85)" />
        <rect x="68" y="74" width="44" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="68" y="86" width="52" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="68" y="98" width="38" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        {/* Total row */}
        <rect x="68" y="116" width="64" height="2" rx="1" fill="hsl(var(--primary) / 0.4)" />
        <rect x="68" y="124" width="22" height="6" rx="2" fill="hsl(var(--primary))" />
        <rect x="106" y="124" width="26" height="6" rx="2" fill="hsl(var(--accent))" />
      </motion.g>

      {/* money 1 */}
      <motion.g
        initial={{ y: -2, rotate: -12 }}
        animate={{ y: 4, rotate: 8 }}
        transition={{ duration: 2.8, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      >
        <circle cx="42" cy="55" r="11" fill="hsl(var(--accent))" stroke="hsl(var(--primary))" strokeWidth="2.5" />
        <text x="42" y="60" textAnchor="middle" fontSize="12" fontWeight="800" fill="hsl(var(--primary))">₹</text>
      </motion.g>

      {/* money 2 */}
      <motion.g
        initial={{ y: 2 }}
        animate={{ y: -6 }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.4 }}
      >
        <circle cx="160" cy="80" r="9" fill="hsl(var(--primary))" stroke="hsl(var(--primary))" strokeWidth="2" />
        <text x="160" y="84" textAnchor="middle" fontSize="10" fontWeight="800" fill="hsl(var(--accent))">₹</text>
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No groups — three avatars with plus                                */
/* ------------------------------------------------------------------ */
export const EmptyGroups = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="170" rx="68" ry="8" fill="hsl(var(--primary) / 0.08)" />

      {/* Big avatar */}
      <motion.g
        initial={{ y: 0 }}
        animate={{ y: -3 }}
        transition={{ duration: 2.6, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      >
        <circle cx="100" cy="80" r="32" fill="hsl(var(--primary))" />
        <circle cx="100" cy="68" r="11" fill="hsl(var(--accent))" />
        <path d="M76 100 Q100 88 124 100 Q124 116 100 116 Q76 116 76 100 Z" fill="hsl(var(--accent))" />
      </motion.g>

      {/* Left avatar */}
      <motion.g
        initial={{ y: 2 }}
        animate={{ y: -2 }}
        transition={{ duration: 2.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.3 }}
      >
        <circle cx="48" cy="120" r="22" fill="hsl(var(--card))" stroke="hsl(var(--primary))" strokeWidth="3" />
        <circle cx="48" cy="113" r="7" fill="hsl(var(--primary))" />
        <path d="M33 132 Q48 124 63 132 Q63 142 48 142 Q33 142 33 132 Z" fill="hsl(var(--primary))" />
      </motion.g>

      {/* Right avatar with + */}
      <motion.g
        initial={{ y: -1 }}
        animate={{ y: 4 }}
        transition={{ duration: 2.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: 0.6 }}
      >
        <circle cx="152" cy="120" r="22" fill="hsl(var(--accent))" stroke="hsl(var(--primary))" strokeWidth="3" strokeDasharray="4 3" />
        <path d="M152 110 L152 130 M142 120 L162 120" stroke="hsl(var(--primary))" strokeWidth="3.5" strokeLinecap="round" />
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* All caught up — checkmark with rays                                */
/* ------------------------------------------------------------------ */
export const AllCaughtUp = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Rays */}
      <motion.g
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '100px 100px' }}
      >
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <line
            key={a}
            x1="100"
            y1="40"
            x2="100"
            y2="52"
            stroke="hsl(var(--accent))"
            strokeWidth="3"
            strokeLinecap="round"
            transform={`rotate(${a} 100 100)`}
          />
        ))}
      </motion.g>

      <ellipse cx="100" cy="170" rx="50" ry="6" fill="hsl(var(--positive) / 0.15)" />

      {/* Big check circle */}
      <motion.g
        initial={{ scale: 0.7 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 180, damping: 12 }}
      >
        <circle cx="100" cy="100" r="44" fill="hsl(var(--positive))" />
        <motion.path
          d="M82 102 L94 114 L120 88"
          stroke="white"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        />
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No notifications — bell with zzz                                    */
/* ------------------------------------------------------------------ */
export const EmptyNotifications = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="170" rx="48" ry="6" fill="hsl(var(--primary) / 0.08)" />

      <motion.g
        initial={{ rotate: -4 }}
        animate={{ rotate: 4 }}
        transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        style={{ transformOrigin: '100px 60px' }}
      >
        <path
          d="M100 60 Q70 60 70 100 L60 130 L140 130 L130 100 Q130 60 100 60 Z"
          fill="hsl(var(--card))"
          stroke="hsl(var(--primary))"
          strokeWidth="3.5"
          strokeLinejoin="round"
        />
        <circle cx="100" cy="58" r="6" fill="hsl(var(--primary))" />
        <path d="M88 138 Q100 148 112 138" stroke="hsl(var(--primary))" strokeWidth="3.5" strokeLinecap="round" fill="none" />
      </motion.g>

      {/* Z's */}
      {[
        { x: 145, y: 70, size: 14, delay: 0 },
        { x: 158, y: 50, size: 18, delay: 0.4 },
        { x: 172, y: 32, size: 22, delay: 0.8 },
      ].map((z, i) => (
        <motion.text
          key={i}
          x={z.x}
          y={z.y}
          fontSize={z.size}
          fontWeight="900"
          fill="hsl(var(--accent))"
          fontFamily="serif"
          initial={{ opacity: 0, y: z.y + 6 }}
          animate={{ opacity: [0, 1, 0], y: z.y - 6 }}
          transition={{ duration: 2.4, repeat: Infinity, delay: z.delay, ease: 'easeOut' }}
        >
          z
        </motion.text>
      ))}
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No comments — chat bubble                                           */
/* ------------------------------------------------------------------ */
export const EmptyComments = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="172" rx="50" ry="6" fill="hsl(var(--primary) / 0.08)" />

      <motion.g {...float}>
        <path
          d="M40 60 Q40 40 60 40 L140 40 Q160 40 160 60 L160 110 Q160 130 140 130 L100 130 L82 148 L82 130 L60 130 Q40 130 40 110 Z"
          fill="hsl(var(--card))"
          stroke="hsl(var(--primary))"
          strokeWidth="3.5"
          strokeLinejoin="round"
        />
        <circle cx="74" cy="84" r="5" fill="hsl(var(--primary))" />
        <circle cx="100" cy="84" r="5" fill="hsl(var(--primary))" />
        <circle cx="126" cy="84" r="5" fill="hsl(var(--primary))" />
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No fund — wallet                                                    */
/* ------------------------------------------------------------------ */
export const EmptyFund = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="172" rx="56" ry="7" fill="hsl(var(--primary) / 0.08)" />

      <motion.g
        initial={{ rotate: -2 }}
        animate={{ rotate: 2 }}
        transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        style={{ transformOrigin: '100px 110px' }}
      >
        {/* Wallet body */}
        <rect x="42" y="70" width="116" height="78" rx="14" fill="hsl(var(--primary))" />
        {/* Flap */}
        <path d="M42 84 L42 82 Q42 64 60 64 L130 64 Q140 64 140 70 L140 84 Z" fill="hsl(var(--accent))" />
        {/* Card slot */}
        <rect x="100" y="104" width="58" height="22" rx="6" fill="hsl(var(--accent))" />
        <circle cx="148" cy="115" r="5" fill="hsl(var(--primary))" />
      </motion.g>

      {/* Falling money */}
      <motion.g
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: [0, 1, 1, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeIn', delay: 0.3 }}
      >
        <circle cx="80" cy="60" r="9" fill="hsl(var(--accent))" stroke="hsl(var(--primary))" strokeWidth="2" />
        <text x="80" y="64" textAnchor="middle" fontSize="10" fontWeight="800" fill="hsl(var(--primary))">₹</text>
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No recurring — clock with arrow                                    */
/* ------------------------------------------------------------------ */
export const EmptyRecurring = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="172" rx="50" ry="6" fill="hsl(var(--primary) / 0.08)" />

      <motion.g
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '100px 100px' }}
      >
        <circle cx="100" cy="100" r="48" fill="none" stroke="hsl(var(--primary))" strokeWidth="3.5" strokeDasharray="6 4" />
      </motion.g>

      <circle cx="100" cy="100" r="38" fill="hsl(var(--card))" stroke="hsl(var(--primary))" strokeWidth="3" />
      {/* Hour hand */}
      <motion.line
        x1="100"
        y1="100"
        x2="100"
        y2="76"
        stroke="hsl(var(--primary))"
        strokeWidth="4"
        strokeLinecap="round"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '100px 100px' }}
      />
      {/* Minute hand */}
      <motion.line
        x1="100"
        y1="100"
        x2="118"
        y2="100"
        stroke="hsl(var(--accent))"
        strokeWidth="4"
        strokeLinecap="round"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '100px 100px' }}
      />
      <circle cx="100" cy="100" r="4" fill="hsl(var(--primary))" />
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No sessions — clipboard with check                                  */
/* ------------------------------------------------------------------ */
export const EmptySessions = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="172" rx="48" ry="6" fill="hsl(var(--primary) / 0.08)" />

      <motion.g {...float}>
        {/* Clipboard back */}
        <rect x="58" y="50" width="84" height="110" rx="10" fill="hsl(var(--primary))" />
        {/* Paper */}
        <rect x="64" y="60" width="72" height="94" rx="6" fill="hsl(var(--card))" />
        {/* Clip */}
        <rect x="84" y="42" width="32" height="14" rx="4" fill="hsl(var(--accent))" />
        {/* Lines */}
        <rect x="74" y="78" width="40" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="74" y="92" width="52" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        <rect x="74" y="106" width="36" height="4" rx="2" fill="hsl(var(--muted-foreground) / 0.5)" />
        {/* Big check */}
        <circle cx="116" cy="128" r="14" fill="hsl(var(--positive))" />
        <path d="M110 128 L114 132 L122 124" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </motion.g>
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* All settled — handshake                                            */
/* ------------------------------------------------------------------ */
export const AllSettled = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="172" rx="56" ry="7" fill="hsl(var(--positive) / 0.12)" />

      <motion.g
        initial={{ scale: 0.85 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 14 }}
      >
        {/* Left arm */}
        <path d="M30 110 L72 90 L102 102 L74 124 Z" fill="hsl(var(--primary))" />
        <rect x="22" y="100" width="22" height="20" rx="4" fill="hsl(var(--accent))" />
        {/* Right arm */}
        <path d="M170 110 L128 90 L98 102 L126 124 Z" fill="hsl(var(--primary))" />
        <rect x="156" y="100" width="22" height="20" rx="4" fill="hsl(var(--accent))" />
        {/* Hands clasping */}
        <ellipse cx="100" cy="105" rx="20" ry="14" fill="hsl(var(--accent))" stroke="hsl(var(--primary))" strokeWidth="2.5" />
      </motion.g>

      {/* Sparkles */}
      {[
        { x: 50, y: 60, d: 0 },
        { x: 150, y: 60, d: 0.4 },
        { x: 100, y: 40, d: 0.2 },
      ].map((s, i) => (
        <motion.g
          key={i}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, delay: s.d, ease: 'easeOut' }}
          style={{ transformOrigin: `${s.x}px ${s.y}px` }}
        >
          <path
            d={`M${s.x} ${s.y - 8} L${s.x + 3} ${s.y - 1} L${s.x + 10} ${s.y} L${s.x + 3} ${s.y + 1} L${s.x} ${s.y + 8} L${s.x - 3} ${s.y + 1} L${s.x - 10} ${s.y} L${s.x - 3} ${s.y - 1} Z`}
            fill="hsl(var(--accent))"
          />
        </motion.g>
      ))}
    </svg>
  </Wrap>
);

/* ------------------------------------------------------------------ */
/* No errors — peaceful flag                                          */
/* ------------------------------------------------------------------ */
export const EmptyErrors = ({ className }) => (
  <Wrap className={className}>
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <ellipse cx="100" cy="170" rx="50" ry="6" fill="hsl(var(--primary) / 0.08)" />
      <line x1="68" y1="40" x2="68" y2="160" stroke="hsl(var(--primary))" strokeWidth="4" strokeLinecap="round" />
      <motion.path
        d="M68 50 Q108 40 144 56 Q108 70 68 60 Z"
        fill="hsl(var(--positive))"
        initial={{ scaleX: 0.95 }}
        animate={{ scaleX: 1.02 }}
        transition={{ duration: 2.4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
        style={{ transformOrigin: '68px 55px' }}
      />
      <text x="106" y="62" textAnchor="middle" fontSize="14" fontWeight="900" fill="white">OK</text>
    </svg>
  </Wrap>
);
